import React, { useContext } from 'react';
import axios from 'axios';
import { Avatar, Icon, Popconfirm, PRONOUN_TAGS, Text } from 'components';

import {
  AuthContext,
  ContactDrawerContext,
  ContextualChatContext,
  CrossOrgAnsweringContext,
} from 'contexts';
import { PopulatedUser } from 'api/UserController/getProfile';
import {
  OrgFeatureMap,
  getUserAddress,
  isAuthForRole,
  isContextualChatEnabled,
} from 'utils';
import { useStartChat } from 'hooks';

import { Pronoun } from '@bb/users/dist/types/enums';
import { SupraCoopType } from '@bb/domain/dist/types';
import styled from 'styled-components';
import {
  ChatButton,
  ChatIcon,
  CompanyText,
  Container,
  EmployerContainer,
  FullProfileName,
  InfoContainer,
  NameContainer,
  PronounText,
  RoleTag,
  RolesContainer,
  TeamText,
} from './common';

interface UserProfileProps {
  contact: PopulatedUser;
  contactFeatureMap: OrgFeatureMap;
}

const chatNotAvailableText =
  'You cannot send messages while assuming a user.\n Please exit the assume state first';

const UserProfile: React.FC<UserProfileProps> = ({
  contact,
  contactFeatureMap,
}) => {
  // For external pages these context will be empty and unneeded
  const authContextValue = useContext(AuthContext);
  const crossOrgAnsweringContextValue = useContext(CrossOrgAnsweringContext);
  const { startChat } = useStartChat();
  const { setChatModalVisible } = useContext(ContextualChatContext);

  const { agentSubscriptionTag, closeContactDrawer } =
    useContext(ContactDrawerContext);
  const [popupVisible, setPopupVisible] = React.useState(false);

  const shouldDisplayChatButton = React.useCallback(() => {
    if (!authContextValue || Object.keys(authContextValue).length === 0)
      return false;
    if (!authContextValue.hasFeature('CHAT')) return false;
    if (contact.id === authContextValue.auth?.id) return false;
    if (contact.isDeleted || contact.disabled) return false;
    if (!contact.org.features.includes('CHAT')) return false;
    if (
      contact.coopType &&
      (contact.coopType === SupraCoopType.AUTO_COOP ||
        (contact.coopType === SupraCoopType.MANUAL_COOP &&
          authContextValue.auth.org.type !== 'mlsStaff'))
    )
      return false;

    if (
      contact.isAgentChatHidden &&
      !isAuthForRole({
        auth: authContextValue.auth,
        org: contact.org.id,
        location: contact.location,
      })
    ) {
      return false;
    }
    return true;
  }, [contact, contactFeatureMap]);

  const pronounTags = contact.pronoun
    ?.map((key: Pronoun) => PRONOUN_TAGS[key].label)
    .join(' | ');

  const contactAddress = getUserAddress(contact);

  const _startNewConvo = async () => {
    await axios.post('/messaging/newConvo', { userIds: [contact.id] });
  };

  const handleOnChatButtonClick = async () => {
    if (authContextValue.auth?.assumerIdMlsStaff) {
      setPopupVisible(true);
    } else if (
      crossOrgAnsweringContextValue &&
      Object.keys(crossOrgAnsweringContextValue).length !== 0
    ) {
      crossOrgAnsweringContextValue.answeringConfirm(
        { type: 'agent', subscriptionTag: agentSubscriptionTag },
        async () => {
          await _startNewConvo();
          closeContactDrawer();
        },
      )();
    }
  };

  const closePopup = () => {
    setPopupVisible(false);
  };

  const useNewChat =
    authContextValue?.hasFeature('CHAT') &&
    authContextValue?.auth?.streamChatId &&
    isContextualChatEnabled(authContextValue?.auth?.org?.mlsTypes);

  const handleNewChatStart = async () => {
    startChat({
      userId: authContextValue?.auth.streamChatId,
      selectedUsers: [{ id: contact.id, bbUserId: contact.id }],
    });
    closeContactDrawer();
    setChatModalVisible(true);
  };

  return (
    <Container>
      <InfoContainer>
        <Avatar user={contact} width={36} />
        <NameContainer>
          <FullProfileName level={2}>
            {`${contact.firstName} ${
              contact.lastName ? contact.lastName : ''
            }`.trim()}
          </FullProfileName>
        </NameContainer>
        {pronounTags && <PronounText>{pronounTags}</PronounText>}
        <RolesContainer>
          <RoleTag color="blue">
            {contact.role.replace('_', ' ').toLocaleLowerCase()}
          </RoleTag>
        </RolesContainer>
        <EmployerContainer>
          <CompanyText>
            {contactAddress.name ? contactAddress.name : contact.org.name}
          </CompanyText>
          {contactFeatureMap.TEAMS && contact.team && (
            <TeamText>{contact.team.name}</TeamText>
          )}
        </EmployerContainer>
      </InfoContainer>
      {shouldDisplayChatButton() && (
        <Popconfirm
          width={'250px'}
          placement="bottom"
          title={chatNotAvailableText}
          okText="OK"
          open={popupVisible}
          cancelButtonProps={{ style: { display: 'none' } }}
          onConfirm={closePopup}
          onCancel={closePopup}
        >
          {useNewChat ? (
            <MessagesButton
              onClick={handleNewChatStart}
              data-cy="contact-chat-button"
            >
              <Icon type="antdMessageFilled" color="white" width={15} />
              <Text color="white" style={{ margin: '0px' }}>
                Message Agent
              </Text>
            </MessagesButton>
          ) : (
            <ChatButton
              type="primary"
              icon={
                <ChatIcon
                  type="chatBubble"
                  height={12}
                  width={13}
                  color="white"
                />
              }
              onClick={handleOnChatButtonClick}
              data-cy="contact-chat-button"
            >
              Send Message
            </ChatButton>
          )}
        </Popconfirm>
      )}
    </Container>
  );
};

export default UserProfile;

const MessagesButton = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  background-color: #2b2b2b;
  color: white;
  padding: 12px 16px;
  z-index: 10; // Needed so that it is in front of the Brokerage Listings table
  border: none;
  border-radius: 25px;
  font-size: 14px;
  font-weight: 600;
`;
